import type { AttackDefinition, CancelRoute, GenericTrapDefinition, GaugeGainSettings } from '../combat/attack';
import { cloneAttackDefinition } from '../combat/attack';
import type { CpuAction, CpuProfile, CpuWeights } from '../cpu/normal-cpu';
import type { BufferedAction } from '../input/input-frame';
import type { LocalAabb } from '../physics/aabb';
import type { CharacterSpatialDefinition, MovementData } from '../physics/spatial-types';

export const SALES_CHARACTER_ID = 'sales';
export const DEVELOPER_CHARACTER_ID = 'developer';

export interface CharacterHurtboxProfile {
  readonly standing: LocalAabb;
  readonly crouching: LocalAabb;
  readonly airborne: LocalAabb;
}

export interface CharacterDefinition {
  readonly id: string;
  readonly displayName: string;
  readonly maxHp: number;
  readonly movement: MovementData;
  readonly pushBox: LocalAabb;
  readonly hurtBox: CharacterHurtboxProfile;
  readonly attacks: readonly AttackDefinition[];
  readonly attackBindings: Readonly<Partial<Record<BufferedAction, string>>>;
  readonly airborneAttackBindings: Readonly<Partial<Record<BufferedAction, string>>>;
  readonly skills: readonly [string, string];
  readonly super: string;
  readonly trapDefinitions: readonly GenericTrapDefinition[];
  readonly cpuProfile: CpuProfile;
}

const box = (minX: number, minY: number, maxX: number, maxY: number): LocalAabb =>
  Object.freeze({ minX, minY, maxX, maxY });
const gauge = (defenderOnHit: number, defenderOnBlock: number): GaugeGainSettings => Object.freeze({
  attackerOnHit: 0, attackerOnBlock: 0, defenderOnHit, defenderOnBlock,
});
const movement = (forwardSpeed: number, backwardSpeed: number, airControlSpeed: number): MovementData => Object.freeze({
  forwardSpeed, backwardSpeed, airControlSpeed, jumpVelocity: -18, gravity: 0.9,
});
const cancels = (targets: readonly string[], startFrame: number, endFrame: number): readonly CancelRoute[] =>
  Object.freeze(targets.flatMap((targetAttackId) => ([
    Object.freeze({ targetAttackId, startFrame, endFrame, condition: 'ON_HIT' as const }),
    Object.freeze({ targetAttackId, startFrame, endFrame, condition: 'ON_BLOCK' as const }),
  ])));

const weights = (values: Partial<Record<CpuAction, number>>): CpuWeights => Object.freeze({
  IDLE: 0, MOVE_FORWARD: 0, MOVE_BACK: 0, JUMP: 0, CROUCH: 0, GUARD: 0,
  LIGHT: 0, HEAVY: 0, SKILL_1: 0, SKILL_2: 0, SUPER: 0,
  ...values,
});
const freezeSkills = (skills: readonly [string, string]): readonly [string, string] =>
  Object.freeze([skills[0], skills[1]]);

export const SALES_CPU_PROFILE: CpuProfile = Object.freeze({
  id: 'sales-normal',
  weights: Object.freeze({
    CLOSE: weights({ LIGHT: 35, HEAVY: 25, SKILL_1: 20, SKILL_2: 10, GUARD: 5, MOVE_BACK: 5 }),
    MID: weights({ HEAVY: 25, SKILL_2: 20, JUMP: 10, GUARD: 10, MOVE_FORWARD: 35 }),
    FAR: weights({ SKILL_2: 30, JUMP: 10, MOVE_FORWARD: 50, IDLE: 10 }),
  }),
  superBonus: Object.freeze({ CLOSE: 40, MID: 30, FAR: 0 }),
});

export const DEVELOPER_CPU_PROFILE: CpuProfile = Object.freeze({
  id: 'developer-normal',
  weights: Object.freeze({
    CLOSE: weights({ LIGHT: 25, SKILL_1: 30, GUARD: 25, MOVE_BACK: 15, IDLE: 5 }),
    MID: weights({ HEAVY: 35, SKILL_1: 15, SKILL_2: 25, GUARD: 15, MOVE_FORWARD: 10 }),
    FAR: weights({ SKILL_2: 45, JUMP: 10, MOVE_FORWARD: 30, IDLE: 15 }),
  }),
  superBonus: Object.freeze({ CLOSE: 40, MID: 30, FAR: 0 }),
  counterAction: 'SKILL_1',
  trapAction: 'SKILL_2',
});

const common = {
  movement: Object.freeze({ x: 0, y: 0 }),
  invulnerability: Object.freeze([]),
} as const;

const salesLightId = 'sales-light';
const salesHeavyId = 'sales-heavy';
const salesAirId = 'sales-air-light';
const salesSkill1Id = 'sales-skill-1';
const salesSkill2Id = 'sales-skill-2';
const salesSuperId = 'sales-super';

const salesAttacks: readonly AttackDefinition[] = Object.freeze([
  cloneAttackDefinition({
    ...common, id: salesLightId, name: '名刺ジャブ', attackType: 'NORMAL', rangeType: 'CLOSE', usage: 'light',
    counterable: true, guardable: true, startupFrames: 5, activeFrames: 3, recoveryFrames: 9,
    damage: 40, chipDamage: 0, hitstunFrames: 12, blockstunFrames: 8, hitstopFrames: 6,
    blockHitstopFrames: 4, knockback: { x: 4, y: 0 }, causesKnockdown: false, gaugeCost: 0,
    gaugeGain: gauge(8, 4), hitboxes: [{ hitId: 'sales-light-hit', box: box(20, -180, 95, -120) }],
    cancelRoutes: cancels([salesHeavyId, salesSkill1Id], 5, 7), effects: [], cinematicId: null,
  }),
  cloneAttackDefinition({
    ...common, id: salesHeavyId, name: 'クロージングストレート', attackType: 'NORMAL', rangeType: 'CLOSE', usage: 'heavy',
    counterable: true, guardable: true, startupFrames: 9, activeFrames: 4, recoveryFrames: 17,
    damage: 85, chipDamage: 0, hitstunFrames: 18, blockstunFrames: 12, hitstopFrames: 9,
    blockHitstopFrames: 6, knockback: { x: 7, y: 0 }, causesKnockdown: false, gaugeCost: 0,
    gaugeGain: gauge(14, 7), hitboxes: [{ hitId: 'sales-heavy-hit', box: box(30, -170, 135, -110) }],
    cancelRoutes: cancels([salesSkill1Id, salesSkill2Id, salesSuperId], 9, 12), effects: [], cinematicId: null,
  }),
  cloneAttackDefinition({
    ...common, id: salesAirId, name: '飛び込み営業', attackType: 'NORMAL', rangeType: 'CLOSE', usage: 'light',
    counterable: true, guardable: true, startupFrames: 7, activeFrames: 6, recoveryFrames: 12,
    damage: 65, chipDamage: 0, hitstunFrames: 14, blockstunFrames: 10, hitstopFrames: 8,
    blockHitstopFrames: 5, knockback: { x: 5, y: 0 }, causesKnockdown: false, gaugeCost: 0,
    gaugeGain: gauge(10, 5), hitboxes: [{ hitId: 'sales-air-hit', box: box(10, -100, 100, 20) }],
    cancelRoutes: [], effects: [], cinematicId: null, airOnly: true, landingRecoveryFrames: 4,
  }),
  cloneAttackDefinition({
    ...common, id: salesSkill1Id, name: '二枚舌ラッシュ', attackType: 'SPECIAL', rangeType: 'CLOSE', usage: 'skill1',
    counterable: true, guardable: true, startupFrames: 8, activeFrames: 16, recoveryFrames: 24,
    damage: 35, chipDamage: 3, hitstunFrames: 14, blockstunFrames: 10, hitstopFrames: 6,
    blockHitstopFrames: 4, knockback: { x: 2, y: 0 }, causesKnockdown: false, gaugeCost: 0,
    gaugeGain: gauge(5, 3), hitboxes: [
      { hitId: 'sales_s1_1', box: box(20, -170, 105, -110), startFrame: 8, endFrame: 9 },
      { hitId: 'sales_s1_2', box: box(25, -175, 115, -115), startFrame: 14, endFrame: 15 },
      {
        hitId: 'sales_s1_3', box: box(30, -180, 130, -105), startFrame: 21, endFrame: 23,
        hitstunFrames: 20, blockstunFrames: 12, hitstopFrames: 8, blockHitstopFrames: 6,
        knockback: { x: 8, y: 0 }, gaugeGain: gauge(7, 4),
      },
    ], cancelRoutes: [], effects: [], cinematicId: null,
    movementWindows: [{ startFrame: 8, endFrame: 22, displacementPerFrame: { x: 3, y: 0 } }],
  }),
  cloneAttackDefinition({
    ...common, id: salesSkill2Id, name: '無理な納期設定', attackType: 'SPECIAL', rangeType: 'FAR', usage: 'skill2',
    counterable: true, guardable: true, startupFrames: 14, activeFrames: 8, recoveryFrames: 18,
    damage: 90, chipDamage: 8, hitstunFrames: 18, blockstunFrames: 10, hitstopFrames: 10,
    blockHitstopFrames: 6, knockback: { x: 9, y: -4 }, causesKnockdown: true, gaugeCost: 0,
    gaugeGain: gauge(14, 7), hitboxes: [{ hitId: 'sales-skill-2-hit', box: box(20, -160, 140, -50) }],
    cancelRoutes: [], effects: [{
      id: 'sales-time-penalty', type: 'TIME_PENALTY', trigger: 'ON_HIT', value: 180,
      oncePerCombo: true, minimumTimerFrames: 300,
    }], cinematicId: null,
    movementWindows: [{ startFrame: 6, endFrame: 20, displacementPerFrame: { x: 6, y: 0 } }],
  }),
  cloneAttackDefinition({
    ...common, id: salesSuperId, name: '「できます！」', attackType: 'SUPER', rangeType: 'FAR', usage: 'super',
    counterable: false, guardable: true, startupFrames: 12, activeFrames: 4, recoveryFrames: 24,
    damage: 260, chipDamage: 20, hitstunFrames: 30, blockstunFrames: 18, hitstopFrames: 14,
    blockHitstopFrames: 10, knockback: { x: 14, y: -8 }, causesKnockdown: true, gaugeCost: 100,
    gaugeGain: gauge(20, 10), hitboxes: [{ hitId: 'sales-super-hit', box: box(20, -180, 160, -40) }],
    cancelRoutes: [], effects: [], cinematicId: salesSuperId,
  }),
]);

const developerLightId = 'developer-light';
const developerHeavyId = 'developer-heavy';
const developerAirId = 'developer-air-light';
const developerCounterId = 'developer-skill-1';
const developerCounterResponseId = 'developer-counter-response';
const developerTrapSpawnId = 'developer-skill-2';
const developerTrapHitId = 'developer-trap-hit';
const developerSuperId = 'developer-super';
export const DEVELOPER_TRAP_DEFINITION_ID = 'developer-trap';

const developerTrap: GenericTrapDefinition = Object.freeze({
  id: DEVELOPER_TRAP_DEFINITION_ID,
  attackId: developerTrapHitId,
  hitbox: box(-40, -30, 40, 0),
  lifetimeFrames: 300,
  offset: Object.freeze({ x: 110, y: 0 }),
  maxActivePerOwner: 1,
});

const developerAttacks: readonly AttackDefinition[] = Object.freeze([
  cloneAttackDefinition({
    ...common, id: developerLightId, name: 'キーボード殴打', attackType: 'NORMAL', rangeType: 'CLOSE', usage: 'light',
    counterable: true, guardable: true, startupFrames: 6, activeFrames: 3, recoveryFrames: 10,
    damage: 45, chipDamage: 0, hitstunFrames: 13, blockstunFrames: 8, hitstopFrames: 6,
    blockHitstopFrames: 4, knockback: { x: 3.5, y: 0 }, causesKnockdown: false, gaugeCost: 0,
    gaugeGain: gauge(8, 4), hitboxes: [{ hitId: 'developer-light-hit', box: box(20, -170, 100, -110) }],
    cancelRoutes: cancels([developerHeavyId, developerCounterId, developerTrapSpawnId], 6, 8), effects: [], cinematicId: null,
  }),
  cloneAttackDefinition({
    ...common, id: developerHeavyId, name: '要件定義書スイング', attackType: 'NORMAL', rangeType: 'FAR', usage: 'heavy',
    counterable: true, guardable: true, startupFrames: 11, activeFrames: 5, recoveryFrames: 19,
    damage: 95, chipDamage: 0, hitstunFrames: 20, blockstunFrames: 13, hitstopFrames: 10,
    blockHitstopFrames: 7, knockback: { x: 8, y: 0 }, causesKnockdown: false, gaugeCost: 0,
    gaugeGain: gauge(14, 7), hitboxes: [{ hitId: 'developer-heavy-hit', box: box(25, -185, 160, -80) }],
    cancelRoutes: cancels([developerTrapSpawnId, developerSuperId], 11, 15), effects: [], cinematicId: null,
  }),
  cloneAttackDefinition({
    ...common, id: developerAirId, name: '障害対応ドロップ', attackType: 'NORMAL', rangeType: 'CLOSE', usage: 'light',
    counterable: true, guardable: true, startupFrames: 8, activeFrames: 8, recoveryFrames: 14,
    damage: 70, chipDamage: 0, hitstunFrames: 15, blockstunFrames: 10, hitstopFrames: 8,
    blockHitstopFrames: 5, knockback: { x: 4, y: 0 }, causesKnockdown: false, gaugeCost: 0,
    gaugeGain: gauge(10, 5), hitboxes: [{ hitId: 'developer-air-hit', box: box(-10, -80, 90, 30) }],
    cancelRoutes: [], effects: [], cinematicId: null, airOnly: true, landingRecoveryFrames: 5,
  }),
  cloneAttackDefinition({
    ...common, id: developerCounterId, name: '仕様変更カウンター', attackType: 'SPECIAL', rangeType: 'CLOSE', usage: 'skill1',
    counterable: false, guardable: false, startupFrames: 3, activeFrames: 18, recoveryFrames: 25,
    damage: 0, chipDamage: 0, hitstunFrames: 0, blockstunFrames: 0, hitstopFrames: 0,
    blockHitstopFrames: 0, knockback: { x: 0, y: 0 }, causesKnockdown: false, gaugeCost: 0,
    gaugeGain: gauge(0, 0), hitboxes: [], cancelRoutes: [], effects: [{
      id: 'developer-counter-window', type: 'COUNTER_WINDOW', trigger: 'ON_FRAME', frame: 3,
      durationFrames: 17, responseAttackId: developerCounterResponseId,
    }], cinematicId: null,
  }),
  cloneAttackDefinition({
    ...common, id: developerCounterResponseId, name: '仕様変更カウンター反撃', attackType: 'SPECIAL', rangeType: 'FAR',
    usage: 'COUNTER_RESPONSE', counterable: false, guardable: false, startupFrames: 4, activeFrames: 4, recoveryFrames: 16,
    damage: 170, chipDamage: 0, hitstunFrames: 24, blockstunFrames: 0, hitstopFrames: 12,
    blockHitstopFrames: 0, knockback: { x: 12, y: -7 }, causesKnockdown: true, gaugeCost: 0,
    gaugeGain: gauge(18, 0), hitboxes: [{ hitId: 'developer-counter-hit', box: box(10, -180, 150, -60) }],
    cancelRoutes: [], effects: [], cinematicId: null, invulnerability: [{ startFrame: 0, endFrame: 23 }],
  }),
  cloneAttackDefinition({
    ...common, id: developerTrapSpawnId, name: 'スパゲッティコード', attackType: 'SPECIAL', rangeType: 'FAR', usage: 'skill2',
    counterable: false, guardable: false, startupFrames: 20, activeFrames: 0, recoveryFrames: 16,
    damage: 0, chipDamage: 0, hitstunFrames: 0, blockstunFrames: 0, hitstopFrames: 0,
    blockHitstopFrames: 0, knockback: { x: 0, y: 0 }, causesKnockdown: false, gaugeCost: 0,
    gaugeGain: gauge(0, 0), hitboxes: [], cancelRoutes: [], effects: [{
      id: 'developer-spawn-trap', type: 'SPAWN_TRAP', trigger: 'ON_FRAME', frame: 20,
      trapDefinitionId: DEVELOPER_TRAP_DEFINITION_ID,
    }], cinematicId: null,
  }),
  cloneAttackDefinition({
    ...common, id: developerTrapHitId, name: 'スパゲッティコード Trap', attackType: 'SPECIAL', rangeType: 'CLOSE', usage: 'TRAP',
    counterable: false, guardable: true, startupFrames: 0, activeFrames: 1, recoveryFrames: 0,
    damage: 50, chipDamage: 0, hitstunFrames: 16, blockstunFrames: 10, hitstopFrames: 8,
    blockHitstopFrames: 6, knockback: { x: 2, y: 0 }, causesKnockdown: false, gaugeCost: 0,
    gaugeGain: gauge(8, 4), hitboxes: [{ hitId: 'trap-contact', box: box(-40, -30, 40, 0) }],
    cancelRoutes: [], effects: [{
      id: 'developer-trap-slow', type: 'SLOW', trigger: 'ON_HIT', strength: 0.5, durationFrames: 120,
    }], cinematicId: null,
  }),
  cloneAttackDefinition({
    ...common, id: developerSuperId, name: '深夜3時の強制ロールバック', attackType: 'SUPER', rangeType: 'FAR', usage: 'super',
    counterable: false, guardable: true, startupFrames: 15, activeFrames: 4, recoveryFrames: 26,
    damage: 270, chipDamage: 20, hitstunFrames: 32, blockstunFrames: 20, hitstopFrames: 14,
    blockHitstopFrames: 10, knockback: { x: 15, y: -9 }, causesKnockdown: true, gaugeCost: 100,
    gaugeGain: gauge(20, 10), hitboxes: [{ hitId: 'developer-super-hit', box: box(30, -190, 170, -50) }],
    cancelRoutes: [], effects: [], cinematicId: developerSuperId,
  }),
]);

const freezeCharacter = (definition: CharacterDefinition): CharacterDefinition => Object.freeze({
  ...definition,
  movement: Object.freeze({ ...definition.movement }),
  pushBox: Object.freeze({ ...definition.pushBox }),
  hurtBox: Object.freeze({
    standing: Object.freeze({ ...definition.hurtBox.standing }),
    crouching: Object.freeze({ ...definition.hurtBox.crouching }),
    airborne: Object.freeze({ ...definition.hurtBox.airborne }),
  }),
  attacks: Object.freeze([...definition.attacks]),
  attackBindings: Object.freeze({ ...definition.attackBindings }),
  airborneAttackBindings: Object.freeze({ ...definition.airborneAttackBindings }),
  skills: freezeSkills(definition.skills),
  trapDefinitions: Object.freeze(definition.trapDefinitions.map((trap) => Object.freeze({
    ...trap, hitbox: Object.freeze({ ...trap.hitbox }), offset: Object.freeze({ ...trap.offset }),
  }))),
});

export const SALES_CHARACTER = freezeCharacter({
  id: SALES_CHARACTER_ID, displayName: 'Sales', maxHp: 1_000,
  movement: movement(5.2, 4.3, 4.6), pushBox: box(-35, -200, 35, 0),
  hurtBox: { standing: box(-45, -220, 45, 0), crouching: box(-50, -140, 50, 0), airborne: box(-40, -190, 40, 10) },
  attacks: salesAttacks,
  attackBindings: { light: salesLightId, heavy: salesHeavyId, skill1: salesSkill1Id, skill2: salesSkill2Id, super: salesSuperId },
  airborneAttackBindings: { light: salesAirId }, skills: [salesSkill1Id, salesSkill2Id], super: salesSuperId,
  trapDefinitions: [], cpuProfile: SALES_CPU_PROFILE,
});

export const DEVELOPER_CHARACTER = freezeCharacter({
  id: DEVELOPER_CHARACTER_ID, displayName: 'Developer', maxHp: 1_000,
  movement: movement(4.3, 3.8, 4), pushBox: box(-40, -190, 40, 0),
  hurtBox: { standing: box(-50, -210, 50, 0), crouching: box(-55, -130, 55, 0), airborne: box(-45, -180, 45, 10) },
  attacks: developerAttacks,
  attackBindings: {
    light: developerLightId, heavy: developerHeavyId, skill1: developerCounterId,
    skill2: developerTrapSpawnId, super: developerSuperId,
  },
  airborneAttackBindings: { light: developerAirId }, skills: [developerCounterId, developerTrapSpawnId], super: developerSuperId,
  trapDefinitions: [developerTrap], cpuProfile: DEVELOPER_CPU_PROFILE,
});

export const MVP_CHARACTER_DEFINITIONS: readonly CharacterDefinition[] = Object.freeze([
  SALES_CHARACTER, DEVELOPER_CHARACTER,
]);

const registry = new Map(MVP_CHARACTER_DEFINITIONS.map((definition) => [definition.id, definition]));

export const getMvpCharacterDefinition = (id: string): CharacterDefinition => {
  const definition = registry.get(id);
  if (definition === undefined) throw new RangeError(`Unknown MVP character: ${id}`);
  return definition;
};

export const toSpatialDefinition = (definition: CharacterDefinition): CharacterSpatialDefinition => Object.freeze({
  pushBox: definition.pushBox,
  movement: definition.movement,
});
